import React, { FC, useState, Suspense } from 'react';
import SkillsGlobe, { Skill } from '../SkillsGlobe';
import { skills as dataSkills, projects as dataProjects } from '../../utils/data';
import useSound from '../../hooks/useSound';
import { ErrorBoundary } from 'react-error-boundary';

// Make sure skills data has id property
const processSkills = (skills: any[]): Skill[] => {
  return skills.map((skill, index) => ({
    ...skill,
    id: skill.id || `skill-${index}`,
  }));
};

const Skills: FC = () => {
  // Get skills from your data source and process them
  const skillsData = processSkills(dataSkills || []);
  const { playClick } = useSound();
  
  // State to track the active skills category for filtering
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  
  // Filter skills by category if one is selected
  const filteredSkills = activeCategory 
    ? skillsData.filter(skill => skill.category === activeCategory)
    : skillsData;
    
  // Group skills by category for the sidebar
  const skillCategories = [...new Set(skillsData.map(skill => skill.category))];
  
  // Make category names more presentable
  const formatCategoryName = (category: string): string => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };
  
  const handleCategoryClick = (category: string) => {
    playClick();
    setActiveCategory(prev => prev === category ? null : category);
  };

  return (
    <section id="skills" className="skills-section">
      <div className="skills-content">
        <h2 className="section-title">Skills</h2>

        <div className="skills-container">
          <div className="skills-text">
            <div className="skills-panel">
              <div className="panel-header">
                <span className="panel-icon">⚙</span>
                <span className="panel-title">TECHNICAL PROFICIENCIES</span>
              </div>

              <div className="panel-content">
                <div className="skills-note">
                  <i className="fas fa-info-circle"></i>
                  <span>Click on skills in the globe to see details & related projects</span>
                </div>

                <div className="categories-filter">
                  <h3>Filter by Category:</h3>
                  <div className="category-buttons">
                    {skillCategories.map(category => (
                      <button 
                        key={category}
                        className={`category-button ${activeCategory === category ? 'active' : ''}`}
                        onClick={() => handleCategoryClick(category)}
                      >
                        {formatCategoryName(category)}
                      </button>
                    ))}
                    {activeCategory && (
                      <button 
                        className="category-button clear-filter"
                        onClick={() => setActiveCategory(null)}
                      >
                        Clear Filter
                      </button>
                    )}
                  </div>
                </div>

                <div className="skills-list-container">
                  <h3>{activeCategory ? `${formatCategoryName(activeCategory)} Skills` : 'Top Skills'}</h3>
                  <div className="skills-list">
                    {(activeCategory ? filteredSkills : skillsData
                      .sort((a, b) => b.level - a.level)
                      .slice(0, 8))
                      .map((skill, index) => (
                        <div className="skill-item" key={skill.id}>
                          <span className="skill-name">{skill.name}</span>
                          <div className="skill-progress">
                            <div className="skill-bar">
                              <div className="skill-level" style={{ width: `${skill.level}%` }}></div>
                            </div>
                            <span className="skill-percentage">{skill.level}%</span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="globe-container">
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
              <ErrorBoundary fallback={<div>Failed to load 3D skills globe</div>}>
                <Suspense fallback={<div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <div>Loading Skills Globe...</div>
                </div>}>
                  <SkillsGlobe skills={filteredSkills} projects={dataProjects} />
                </Suspense>
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .skills-section {
          min-height: 100vh;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 80px 0;
          position: relative;
          z-index: 1;
        }

        .skills-content {
          width: 100%;
          max-width: 1200px;
          padding: 0 20px;
        }

        .section-title {
          font-size: 42px;
          color: #00ff00;
          margin-bottom: 40px;
          text-align: center;
          text-shadow: 0 0 10px rgba(0, 255, 0, 0.7);
          font-family: 'VT323', monospace;
          letter-spacing: 3px;
        }

        .skills-container {
          display: flex;
          justify-content: center;
          align-items: stretch;
          gap: 30px;
          flex-wrap: wrap;
        }

        .skills-text {
          flex: 1;
          min-width: 300px;
        }

        .skills-panel {
          background-color: rgba(0, 0, 0, 0.7);
          border: 1px solid #00ff00;
          border-radius: 5px;
          overflow: hidden;
          height: 100%;
          box-shadow: 0 0 15px rgba(0, 255, 0, 0.5);
        }

        .panel-header {
          background-color: rgba(0, 50, 0, 0.5);
          padding: 15px;
          display: flex;
          align-items: center;
          gap: 10px;
          border-bottom: 1px solid #00ff00;
        }

        .panel-icon {
          font-size: 20px;
          color: #00ff00;
        }

        .panel-title {
          font-family: 'VT323', monospace;
          font-size: 22px;
          letter-spacing: 2px;
          color: #00ff00;
        }

        .panel-content {
          padding: 20px;
        }

        .globe-container {
          flex: 1;
          min-width: 300px;
          height: 500px;
          position: relative;
          z-index: 2;
        }

        .skills-note {
          background-color: rgba(0, 255, 0, 0.1);
          border: 1px solid rgba(0, 255, 0, 0.3);
          padding: 10px;
          border-radius: 5px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .categories-filter {
          margin-top: 20px;
          margin-bottom: 20px;
        }

        .categories-filter h3 {
          color: #00ff00;
          font-family: 'VT323', monospace;
          font-size: 18px;
          margin-bottom: 10px;
        }

        .category-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 15px;
        }

        .category-button {
          background-color: transparent;
          border: 1px solid #00ff00;
          color: #00ff00;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-family: 'Courier Prime', monospace;
          font-size: 12px;
          transition: all 0.2s;
        }

        .category-button:hover {
          background-color: rgba(0, 255, 0, 0.1);
          box-shadow: 0 0 8px rgba(0, 255, 0, 0.5);
        }

        .category-button.active {
          background-color: rgba(0, 255, 0, 0.2);
          box-shadow: 0 0 10px rgba(0, 255, 0, 0.6);
          font-weight: bold;
        }

        .category-button.clear-filter {
          background-color: rgba(255, 0, 0, 0.1);
          border-color: rgba(255, 0, 0, 0.5);
          color: rgba(255, 0, 0, 0.8);
        }

        .category-button.clear-filter:hover {
          background-color: rgba(255, 0, 0, 0.2);
          box-shadow: 0 0 8px rgba(255, 0, 0, 0.5);
        }

        .skills-list-container {
          margin-top: 20px;
        }

        .skills-list-container h3 {
          color: #00ff00;
          font-family: 'VT323', monospace;
          font-size: 20px;
          margin-bottom: 15px;
        }

        .skills-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .skill-item {
          display: flex;
          flex-direction: column;
          gap: 5px;
          position: relative;
          margin-bottom: 18px; /* Consistent spacing between skill items */
        }

        .skill-name {
          color: #00ff00;
          font-size: 16px;
          display: flex;
          justify-content: space-between;
          margin-bottom: 3px; /* Added space before progress bar */
        }

        .skill-progress {
          position: relative;
          display: flex;
          align-items: center;
          height: 20px; /* Increased height for better alignment */
        }

        .skill-bar {
          height: 8px;
          background-color: rgba(0, 255, 0, 0.1);
          border-radius: 4px;
          overflow: hidden;
          flex-grow: 1;
          width: calc(100% - 60px); /* Slightly increased to match mobile view */
          margin-right: 10px; /* Space before percentage */
        }

        .skill-level {
          height: 100%;
          background: linear-gradient(90deg, #003300, #00ff00);
          border-radius: 4px;
        }

        .skill-percentage {
          color: #00ff00;
          font-size: 15px; /* Slightly larger font */
          font-weight: bold;
          white-space: nowrap;
          width: 50px; /* Increased to match mobile view */
          text-align: right;
          display: inline-block;
          background-color: rgba(0, 20, 0, 0.7);
          border-radius: 4px;
          padding: 2px 6px; /* Increased padding */
          box-shadow: 0 0 8px rgba(0, 255, 0, 0.5); /* Enhanced glow */
          position: absolute;
          right: 0;
        }

        @media (max-width: 768px) {
          .skills-container {
            flex-direction: column-reverse;
          }

          .globe-container {
            height: 400px;
          }
          
          .skill-item {
            margin-bottom: 22px; /* Even more space between items on mobile */
          }
          
          .skill-progress {
            height: 24px; /* Increased height for better touch targets */
          }
          
          .skill-bar {
            width: calc(100% - 60px); /* Slightly wider percentage area on mobile */
          }
          
          .skill-percentage {
            right: 0;
            width: 50px; /* Wider on mobile */
            font-size: 16px; /* Larger font on mobile */
            text-align: center;
            padding: 2px 5px;
          }
        }
        
        @media (max-width: 350px) {
          /* Extra small screens */
          .skill-progress {
            position: relative;
          }
          
          .skill-percentage {
            position: absolute;
            right: 0;
            top: -20px;
          }
          
          .skill-bar {
            max-width: 100%;
            margin-right: 0;
          }
        }
      `}</style>
    </section>
  );
};

export default Skills;